import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  CircularProgress,
  Backdrop,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import PackagesForm from './PackageForm';
import AddBenefitWizard from './AddBenefitWizard';
import { getRatingNodeLabel } from '../shared/allowedRelationships';

import { Add, Close, LocalOffer, Edit, Delete, ExpandMore } from '@material-ui/icons';

const PackagesTab = (props) => {
  const [openPackageModal, setOpenPackageModal] = useState(false);
  const [openBenefitsModal, setOpenBenefitsModal] = useState(false);
  const [benefitsModalKey, setBenefitsModalKey] = useState(0);
  const [selectedPackageForBenefits, setSelectedPackageForBenefits] = useState(null);
  const [selectedPackageForEdit, setSelectedPackageForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [selectedBenefitForEdit, setSelectedBenefitForEdit] = useState(null);
  const [benefitDeleteConfirmOpen, setBenefitDeleteConfirmOpen] = useState(false);
  const [benefitToDelete, setBenefitToDelete] = useState(null);
  // Which package is being edited inline (by code) — inline form instead of modal.
  const [editingPackageCode, setEditingPackageCode] = useState(null);

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle package success states
  useEffect(() => {
    if (props.isSuccess) {
      // RatingNode creation is always maker-checker gated (see saga.es6.js),
      // so a package created with a premium amount usually can't link its
      // premium_rating_node_guid yet — say so instead of a plain "success"
      // that leaves the premium looking silently dropped.
      const pendingCrGuid = props.createdPackage && props.createdPackage.premium_change_request_guid;
      setSnackbarMessage(
        pendingCrGuid
          ? `Package created. Premium submitted for approval (change request ${pendingCrGuid}) — it will apply automatically once approved.`
          : 'Package operation completed successfully!'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenPackageModal(false);
      setSelectedPackageForEdit(null);

      // Reset success state after showing notification
      setTimeout(() => {
        props.resetPackageSuccess && props.resetPackageSuccess();
      }, 100);
    }
  }, [props.isSuccess]);

  // Handle package error states
  useEffect(() => {
    if (props.isError) {
      setSnackbarMessage(`Error: ${props.isError.message || 'Package operation failed'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetPackageError && props.resetPackageError();
      }, 100);
    }
  }, [props.isError]);

  // Handle benefits success states
  useEffect(() => {
    if (props.benefitsSuccess) {
      setSnackbarMessage('Benefits operation completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenBenefitsModal(false);
      setSelectedBenefitForEdit(null);
      
      // Reset success state after showing notification
      setTimeout(() => {
        props.resetBenefitsSuccess && props.resetBenefitsSuccess();
      }, 100);
    }
  }, [props.benefitsSuccess]);

  // Handle benefits error states
  useEffect(() => {
    if (props.benefitsError) {
      setSnackbarMessage(`Error: ${props.benefitsError.message || 'Benefits operation failed'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetBenefitsError && props.resetBenefitsError();
      }, 100);
    }
  }, [props.benefitsError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const currency = props.currency;

  // `section` decides what this instance renders, so the same component (and all
  // its modals / save dispatches) can back both the Packages tab (package-level
  // config + a compact benefit summary) and the Benefits & Rating Nodes tab
  // (benefits grouped by package, each with its rating slots). Defaults to
  // 'packages' for backward compatibility.
  const section = props.section === 'benefits' ? 'benefits' : 'packages';
  const showPackages = section === 'packages';
  const showBenefits = section === 'benefits';

  const ratingNodes = props.ratingNodes || [];

  // Legacy sentinel match: a package's premium node keyed by
  // (package_code, '_package', 'premium') on the RatingNode. Kept as the
  // display fallback for packages that predate the explicit FK below.
  const findPackageNode = (pkgCode) =>
    ratingNodes.find(
      (n) =>
        (n.package_code || null) === (pkgCode || null) &&
        n.benefit_code === '_package' &&
        (!n.applies_to || n.applies_to === 'premium')
    );

  // All candidate premium nodes for the package slot picker.
  const packagePremiumNodes = ratingNodes.filter(
    (n) => n.benefit_code === '_package' && (!n.applies_to || n.applies_to === 'premium')
  );

  // Inline change of the package's premium_rating_node_guid FK — first-class,
  // like the benefit rating slots. Dispatches the same clean payload shape
  // PackageForm submits (whitelist-safe) plus the FK.
  const handlePackageRatingChange = (pkg, value) => {
    if (!props.handleEditPackage) return;
    const payload = {
      name: pkg.name,
      code: pkg.code,
      premium_rating_node_guid: value || null,
      package_guid: pkg.guid || pkg.package_guid,
    };
    if (pkg.premium_granularity) payload.premium_granularity = pkg.premium_granularity;
    if (pkg.premium_cardinality != null) payload.premium_cardinality = pkg.premium_cardinality;
    props.handleEditPackage(payload);
  };


  const formatCurrency = (amountInCents) => {
    if (!amountInCents && amountInCents !== 0) return 'N/A';
    var units = (amountInCents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? currency + ' ' + units : units;
  };

  const benefitValue = (b) => {
    if (b.benefit_kind === 'quantity') {
      const n = b.benefit_amount_in_cents || 0;
      return `${n} unit${n === 1 ? '' : 's'}${b.benefit_granularity ? ` per ${b.benefit_granularity}` : ''}`;
    }
    const hasFixedAmount = b.benefit_amount_in_cents !== undefined && b.benefit_amount_in_cents !== null;
    // Member-chosen sum-assured band (amount_min_cents/amount_max_cents, no static
    // benefit_amount_in_cents) — show the band ceiling instead of N/A.
    if (!hasFixedAmount && b.amount_max_cents !== undefined && b.amount_max_cents !== null) {
      return `Up to ${formatCurrency(b.amount_max_cents)}${b.benefit_granularity ? ` per ${b.benefit_granularity}` : ''}`;
    }
    // Priced via a rating node/table (Per rule or hand-built) rather than a
    // flat amount — "N/A" would misread as "nothing configured".
    if (!hasFixedAmount && b.cover_rating_node_guid) {
      return 'Varies by relationship (rating table)';
    }
    return `${formatCurrency(b.benefit_amount_in_cents)}${b.benefit_granularity ? ` per ${b.benefit_granularity}` : ''}`;
  };

  const waitingPeriodValue = (b) => {
    if (b.waiting_period_rating_node_guid) return 'Varies by relationship (rating table)';
    if (!b.waiting_period_cardinality) return 'None';
    return `${b.waiting_period_cardinality} ${b.waiting_period_granularity || 'Day'}${b.waiting_period_cardinality === 1 ? '' : 's'}`;
  };

  const premiumValue = (b) => {
    if (b.premium_rating_node_guid) return 'Varies by relationship (rating table)';
    const hasFixedAmount = b.premium_amount_in_cents !== undefined && b.premium_amount_in_cents !== null;
    if (!hasFixedAmount) return 'Inherits package premium';
    return `${formatCurrency(b.premium_amount_in_cents)}${b.premium_granularity ? ` per ${b.premium_granularity}` : ''}`;
  };

  const handleOpenPackageModal = (packageData = null) => {
    console.log("Opening package modal with data:", packageData);
    setSelectedPackageForEdit(packageData);
    setOpenPackageModal(true);
  };

  const handleClosePackageModal = () => {
    console.log("Closing package modal and resetting state");
    setOpenPackageModal(false);
    setSelectedPackageForEdit(null);
  };

  const handleOpenBenefitsModal = (packageData = null, benefitData = null) => {
    setSelectedPackageForBenefits(packageData);
    setSelectedBenefitForEdit(benefitData);
    setBenefitsModalKey(function(k) { return k + 1; });
    setOpenBenefitsModal(true);
  };

  const handleCloseBenefitsModal = () => {
    setOpenBenefitsModal(false);
    setSelectedPackageForBenefits(null);
    setSelectedBenefitForEdit(null);
    props.resetBenefitsError && props.resetBenefitsError();
    props.resetBenefitRatingChainState && props.resetBenefitRatingChainState();
    props.resetUpdateBenefitRatingChainState && props.resetUpdateBenefitRatingChainState();
  };

  const handleCreatePackage = (data) => {
    if (selectedPackageForEdit) {
      // Edit mode
      if (props.handleEditPackage) {
        console.log("Editing Package from PackagesTab:", data);
        const editData = {
          ...data,
          package_guid: selectedPackageForEdit.guid || selectedPackageForEdit.package_guid,
        };
        props.handleEditPackage(editData);
      }
    } else {
      // Create mode
      if (props.handleAddPackage) {
        console.log("Creating Package from PackagesTab:", data);
        props.handleAddPackage(data);
      }
    }
    
    // Note: Modal will be closed in success handler, not here
  };

  const handleCreateBenefits = (data) => {
    if (props.handleAddBenefits) {
      console.log("Adding Benefits from PackagesTab:", data);
      const benefitData = {
        ...data,
        package_guid: selectedPackageForBenefits && selectedPackageForBenefits.guid
      };
      props.handleAddBenefits(benefitData);
    }

    // Note: Modal will be closed in success handler, not here
  };

  // AddBenefitWizard's "Per rule" path — hands off to the rating-chain saga
  // (docs/2026-09-04-benefit-pricing-wizard-design.md), which itself hands
  // off to handleAddBenefits' own action once the chain succeeds, so the
  // same success/close-modal effect above covers this path too.
  const handleCreateBenefitWithRules = ({ targets, benefitPayload }) => {
    if (props.handleAddBenefitWithRules) {
      props.handleAddBenefitWithRules({
        targets,
        benefitPayload,
        package_guid: selectedPackageForBenefits && selectedPackageForBenefits.guid
      });
    }
  };

  const handleEditBenefits = (data) => {    
    if (props.handleEditPackageBenefits) {
      console.log("Editing Benefits from PackagesTab:", data);
      const benefitData = {
        ...data,
        package_guid: selectedPackageForBenefits && selectedPackageForBenefits.guid,
        benefit_guid: selectedBenefitForEdit.guid
      };
      props.handleEditPackageBenefits(benefitData);
    }
    
    // Note: Modal will be closed in success handler, not here
  };

  // AddBenefitWizard's edit-mode "Per rule" path — hands off to the update
  // rating-chain saga (docs/2026-09-04-benefit-pricing-wizard-design.md §9.3).
  const handleUpdateBenefitWithRules = ({ createTargets, updateTargets, benefitPayload }) => {
    if (props.handleUpdateBenefitWithRules) {
      props.handleUpdateBenefitWithRules({
        createTargets,
        updateTargets,
        benefitPayload,
        package_guid: selectedPackageForBenefits && selectedPackageForBenefits.guid,
        benefit_guid: selectedBenefitForEdit && selectedBenefitForEdit.guid
      });
    }
  };

  const handleDeletePackage = (package_guid) => {
    if (props.handleDeleteProductPackages) {
      console.log("Deleting Package from PackagesTab:", package_guid);
      props.handleDeleteProductPackages(package_guid);
    }
  };

  const handleDeleteClick = (pkg) => {
    setPackageToDelete(pkg);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (packageToDelete) {
      handleDeletePackage(packageToDelete.guid);
      setDeleteConfirmOpen(false);
      setPackageToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPackageToDelete(null);
  };

  const handleBenefitDeleteClick = (benefit, pkg) => {
    setBenefitToDelete({ benefit, package: pkg });
    setBenefitDeleteConfirmOpen(true);
  };

  const handleConfirmBenefitDelete = () => {
    if (benefitToDelete && props.handleDeleteBenefits) {
      console.log("Deleting Benefit from PackagesTab:", benefitToDelete.benefit.guid);
      props.handleDeleteBenefits(benefitToDelete.benefit.guid);
      setBenefitDeleteConfirmOpen(false);
      setBenefitToDelete(null);
    }
  };

  const handleCancelBenefitDelete = () => {
    setBenefitDeleteConfirmOpen(false);
    setBenefitToDelete(null);
  };

  const hasPackages = props.packages && props.packages.length > 0;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {showBenefits ? "Benefits & Rating Nodes" : "Package Configuration"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showBenefits
              ? "Benefits grouped by package — each carries up to three rating nodes (Cover / Premium / Waiting Period)"
              : "Define coverage tiers and their package-level pricing"}
          </Typography>
        </Box>
        {showPackages && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenPackageModal(null)}
             sx={{
              textTransform: "none",
              backgroundColor: "#fba92c",
              "&:hover": {
                backgroundColor: "#fba82c",
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
            }}
            >
              Add Package
            </Button>
          </Stack>
        )}
      </Box>

      {hasPackages ? (
        <Box>
          <Stack spacing={3}>
            {props.packages.map((pkg, index) => (
              <Card 
                key={index} 
                sx={{ 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "##C9C9C9",
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F8F9FA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <LocalOffer sx={{ fontSize: 20, color: "#173E4B" }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {pkg.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          {pkg.code}
                        </Typography>
                        {pkg.premium_amount_in_cents ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {formatCurrency(pkg.premium_amount_in_cents)} premium
                            {pkg.premium_granularity
                              ? ` • per ${pkg.premium_cardinality || 1} ${pkg.premium_granularity}`
                              : ""}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                    {showPackages && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{ color: editingPackageCode === pkg.code ? "#173E4B" : "#6b7280" }}
                          onClick={() => setEditingPackageCode(editingPackageCode === pkg.code ? null : pkg.code)}
                          title={editingPackageCode === pkg.code ? "Close editor" : "Edit package"}
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: "#ef4444" }}
                          onClick={() => handleDeleteClick(pkg)}
                          title="Delete package"
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {showPackages && editingPackageCode === pkg.code && (
                    <Box sx={{ mt: 2, p: 2, border: "1px solid #e5e7eb", borderRadius: 2, backgroundColor: "#fafafa" }}>
                      <PackagesForm
                        handleAddPackage={(data) => {
                          if (props.handleEditPackage) {
                            props.handleEditPackage({ ...data, package_guid: pkg.guid || pkg.package_guid });
                          }
                          setEditingPackageCode(null);
                        }}
                        initialData={pkg}
                        isEditMode={true}
                        currency={currency}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setEditingPackageCode(null)}
                          sx={{ textTransform: "none", color: "#6b7280" }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {showPackages && editingPackageCode !== pkg.code && (() => {
                    const sentinelNode = findPackageNode(pkg.code);
                    const fkGuid = pkg.premium_rating_node_guid || "";
                    const rated = Boolean(fkGuid || sentinelNode);
                    return (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          border: "1px solid #e5e7eb",
                          borderRadius: 1.5,
                          backgroundColor: rated ? "#f0fdf4" : "#fafafa",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#173E4B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Premium Rating Node
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 280 }}>
                          <Select
                            displayEmpty
                            value={fkGuid}
                            onChange={(e) => handlePackageRatingChange(pkg, e.target.value)}
                            sx={{ backgroundColor: "white" }}
                            renderValue={(val) => {
                              if (val) return getRatingNodeLabel(val, ratingNodes);
                              if (sentinelNode)
                                return getRatingNodeLabel(sentinelNode.guid, ratingNodes) + "  (via _package match)";
                              return <span style={{ color: "#999" }}>Not rated — static premium applies</span>;
                            }}
                          >
                            <MenuItem value="">
                              <em>None (fall back to _package match / static premium)</em>
                            </MenuItem>
                            {packagePremiumNodes.map((n) => (
                              <MenuItem key={n.guid} value={n.guid}>
                                {getRatingNodeLabel(n.guid, ratingNodes)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {!fkGuid && sentinelNode && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            Matched by code — select it to link explicitly.
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}

                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Benefits
                      </Typography>
                      <Chip 
                        label={`${(pkg.benefits && pkg.benefits.length) || 0} benefits`} 
                        size="small" 
                        sx={{
                          ...(pkg.benefits && pkg.benefits.length > 0 && {
                            backgroundColor: "#F8F9FA",
                            borderColor: "#173E4B",
                            color: "#b45309",
                          })
                        }}
                        variant="outlined"
                      />
                    </Box>
                    
                    {showPackages ? (
                      <Typography variant="body2" color="text.secondary">
                        {pkg.benefits && pkg.benefits.length
                          ? pkg.benefits.map((b) => b.name).join(", ")
                          : "No benefits linked yet — add them from the Benefits & Rating Nodes tab."}
                      </Typography>
                    ) : pkg.benefits && pkg.benefits.length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {pkg.benefits.map((benefit, bIndex) => (
                          <Accordion key={bIndex} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px !important", "&:before": { display: "none" } }}>
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{ 
                                px: 2, 
                                py: 1,
                                minHeight: "48px",
                                "&.Mui-expanded": { minHeight: "48px" }
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {benefit.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {benefitValue(benefit)} benefit • {formatCurrency(benefit.premium_amount_in_cents)} premium
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, mr: 1 }} onClick={(e) => e.stopPropagation()}>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#173E4B" }}
                                    onClick={() => handleOpenBenefitsModal(pkg, benefit)}
                                    title="Edit benefit"
                                  >
                                    <Edit sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#ef4444" }}
                                    onClick={() => handleBenefitDeleteClick(benefit, pkg)}
                                    title="Delete benefit"
                                  >
                                    <Delete sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                              {/* Read-only summary — actual editing happens via the
                                  Edit button above, which opens the wizard (pre-filled,
                                  if the current rating config is wizard-shaped) or the
                                  classic form as a fallback (§9.2). */}
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 2 }}>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Benefit Amount</Typography>
                                  <Typography variant="body2">{benefitValue(benefit)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Waiting Period</Typography>
                                  <Typography variant="body2">{waitingPeriodValue(benefit)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Premium</Typography>
                                  <Typography variant="body2">{premiumValue(benefit)}</Typography>
                                </Box>
                              </Box>
                              {benefit.benefit_type && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                  Type: {benefit.benefit_type}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                Relationships: {(benefit.allowed_relationships || []).map((r) => r.relationship).join(", ") || "None"}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ 
                        border: "2px dashed #e5e7eb", 
                        borderRadius: 2, 
                        p: 3, 
                        textAlign: "center",
                        backgroundColor: "#fafafa"
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
                          No benefits added yet
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click "Add Benefit" below to get started
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>

                {showBenefits && (
                  <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleOpenBenefitsModal(pkg)}
                      sx={{
                        textTransform: "none",
                        color: "#173E4B",
                        "&:hover": {
                          backgroundColor: "#F8F9FA",
                        }
                      }}
                    >
                      Add Benefit
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px dashed #d1d5db",
            borderRadius: 2,
            p: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <LocalOffer sx={{ fontSize: 24, color: "#6b7280" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No packages yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Get started by creating your first package.<br />
            Once you have packages, you can add benefits to them.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleOpenPackageModal(null)}
            sx={{
              textTransform: "none",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              px: 3,
              py: 1,
            }}
          >
            Create Package
          </Button>
        </Box>
      )}

      {/* Package Creation Modal */}
      <Dialog
        open={openPackageModal && !props.isLoading}
        onClose={handleClosePackageModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
            <IconButton onClick={handleClosePackageModal} size="small" sx={{ mt: -1 }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          <PackagesForm
            handleAddPackage={handleCreatePackage}
            initialData={selectedPackageForEdit}
            isEditMode={!!selectedPackageForEdit}
            currency={currency}
          />
        </DialogContent>
      </Dialog>

      {/* Benefits Creation Modal */}
      <Dialog
        open={openBenefitsModal}
        onClose={handleCloseBenefitsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
            <IconButton onClick={handleCloseBenefitsModal} size="small" sx={{ mt: -1 }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          {/* §9.2 revised: the wizard handles both create and edit
              unconditionally — no fallback to the classic BenefitsForm.
              A target whose current rating config isn't wizard-shaped
              (deriveEditState's decompile failing) shows a warning instead
              of silently misrepresenting it — see AddBenefitWizard.js. */}
          <AddBenefitWizard
            key={benefitsModalKey}
            isEdit={Boolean(selectedBenefitForEdit)}
            initialData={selectedBenefitForEdit}
            ratingNodes={props.ratingNodes || []}
            ratingTables={props.ratingTables || []}
            onSubmit={handleCreateBenefits}
            onSubmitWithRules={handleCreateBenefitWithRules}
            onUpdateBenefit={handleEditBenefits}
            onUpdateBenefitWithRules={handleUpdateBenefitWithRules}
            packageCode={selectedPackageForBenefits && selectedPackageForBenefits.code}
            currency={currency}
            isLoading={props.benefitsLoader}
            chainLoader={selectedBenefitForEdit ? props.updateBenefitRatingChainLoader : props.benefitRatingChainLoader}
            chainError={selectedBenefitForEdit ? props.updateBenefitRatingChainError : props.benefitRatingChainError}
            chainProgress={selectedBenefitForEdit ? props.updateBenefitRatingChainProgress : props.benefitRatingChainProgress}
            chainPending={props.updateBenefitRatingChainPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen && !props.isLoading}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Package
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the package "{packageToDelete && packageToDelete.name}"? 
            This will also remove all associated benefits. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Benefit Delete Confirmation Dialog */}
      <Dialog
        open={benefitDeleteConfirmOpen && !props.benefitsLoader}
        onClose={handleCancelBenefitDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Benefit
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the benefit "{benefitToDelete && benefitToDelete.benefit && benefitToDelete.benefit.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelBenefitDelete}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmBenefitDelete}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop for Packages */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.isLoading || (props.benefitsLoader && !openBenefitsModal)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {props.isLoading ? 'Processing package operation...' : 'Processing benefits operation...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PackagesTab;